import React from 'react';
import { Button } from 'primereact/button';
import {
    SP_SKIP, extractResponseData, SmartStyles, SmartHeader, SmartSection, NoRecordsCard
} from './_SmartFieldRenderer';

// The provider returns matches under `search_data`, not `results` — the old key
// matched nothing, so every search rendered as an unlabelled blob under OTHER
// DETAILS. Criteria are split out from matches so the reader can tell what was
// asked from what came back. Kept in step with TanCompanySearchPdfController.
const GROUPS = {
    criteria: { title: 'SEARCH CRITERIA', icon: 'pi-search', keys: ['search_company_name', 'search_state', 'exact_match', 'hits'] },
    results:  { title: 'SEARCH RESULTS',  icon: 'pi-list',   keys: ['search_data', 'results'] },
};

const HIGHLIGHT = [];

const TanCompanySearchOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details. Please verify and try again." />;
    }

    const d = responseData;
    const grouped = {};
    for (const [groupName, group] of Object.entries(GROUPS)) {
        const picked = {};
        group.keys.forEach(k => { if (k in d && d[k] !== null && d[k] !== undefined && d[k] !== '') picked[k] = d[k]; });
        if (Object.keys(picked).length > 0) grouped[groupName] = picked;
    }
    const matchCount = (Array.isArray(d.search_data) ? d.search_data : Array.isArray(d.results) ? d.results : []).length;
    const knownKeys = new Set(Object.values(GROUPS).flatMap(g => g.keys));
    const leftover = {};
    for (const [k, v] of Object.entries(d)) {
        if (SP_SKIP.has(k) || knownKeys.has(k)) continue;
        if (v === null || v === undefined || v === '') continue;
        leftover[k] = v;
    }

    return (
        <div>
            <SmartStyles />
            <SmartHeader title="TAN Company Search Report" icon="pi-building" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />
            <div className="horizontal-report-card">
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button
                                label="Download PDF"
                                icon="pi pi-file-pdf"
                                size="small"
                                outlined
                                onClick={onDownloadPDF}
                            />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}
                {/* A search that matched nothing is still a completed, charged
                    lookup. Saying so plainly beats an empty SEARCH RESULTS
                    heading that reads like the page failed to load. */}
                {matchCount === 0 && (
                    <div className="surface-100 border-round border-left-3 border-orange-500 p-2 mb-2 flex align-items-start gap-2">
                        <i className="pi pi-info-circle text-orange-500 text-sm mt-1" aria-hidden="true" />
                        <span className="text-600 text-sm line-height-3">
                            No companies matched these search criteria. The lookup was performed, so credits were used.
                        </span>
                    </div>
                )}
                {Object.entries(GROUPS).map(([groupName, meta]) => (
                    grouped[groupName]
                        ? <SmartSection key={groupName} title={meta.title} icon={meta.icon} data={grouped[groupName]} highlightKeys={HIGHLIGHT} />
                        : null
                ))}
                {Object.keys(leftover).length > 0 && (
                    <SmartSection title="OTHER DETAILS" icon="pi-info-circle" data={leftover} />
                )}
            </div>
        </div>
    );
};

export default TanCompanySearchOutputComponent;
